import type { QualityTier } from "./types";

export interface QualityBudgets {
  tier: QualityTier;
  maxPixelRatio: number;
  shadows: boolean;
  /** Max human instances loaded / simulated for this tier (groups 3–6). */
  maxHumans: number;
  /** Max humans that may become dynamic ragdolls (tier gate). */
  maxDynamic: number;
  /** Max total point-cloud samples after LOD (dense + joints). */
  maxPoints: number;
  /** Fixed physics step rate (Hz). */
  physicsHz: number;
}

/** localStorage key for user tier override (H7). */
export const TIER_STORAGE_KEY = "acro.humanScene.tier";

const budgets: Record<QualityTier, QualityBudgets> = {
  low: {
    tier: "low",
    maxPixelRatio: 1,
    shadows: false,
    maxHumans: 2,
    maxDynamic: 1,
    maxPoints: 50_000,
    physicsHz: 30,
  },
  medium: {
    tier: "medium",
    maxPixelRatio: 1.5,
    shadows: false,
    maxHumans: 4,
    maxDynamic: 2,
    maxPoints: 250_000,
    physicsHz: 45,
  },
  high: {
    tier: "high",
    maxPixelRatio: 2,
    shadows: true,
    maxHumans: 6,
    maxDynamic: 6,
    maxPoints: 1_000_000,
    physicsHz: 60,
  },
};

export function isQualityTier(value: unknown): value is QualityTier {
  return value === "low" || value === "medium" || value === "high";
}

/** Read user override from localStorage; null if unset/invalid. */
export function readStoredTier(): QualityTier | null {
  try {
    const v = localStorage.getItem(TIER_STORAGE_KEY);
    return isQualityTier(v) ? v : null;
  } catch {
    return null;
  }
}

/** Persist user tier override; pass null to clear. */
export function writeStoredTier(tier: QualityTier | null): void {
  try {
    if (tier == null) localStorage.removeItem(TIER_STORAGE_KEY);
    else localStorage.setItem(TIER_STORAGE_KEY, tier);
  } catch {
    // private mode / blocked storage — ignore
  }
}

/** Heuristic tier from hardwareConcurrency + mobile UA. */
export function detectQualityTier(): QualityTier {
  try {
    const cores = navigator.hardwareConcurrency || 4;
    const ua = navigator.userAgent || "";
    const mobile = /Mobi|Android|iPhone|iPad/i.test(ua);
    if (mobile || cores <= 4) return "low";
    if (cores <= 8) return "medium";
    return "high";
  } catch {
    return "medium";
  }
}

/**
 * Resolve effective tier: explicit non-auto wins, else localStorage override,
 * else hardware heuristic.
 */
export function resolveQualityTier(explicit?: QualityTier | "auto" | null): QualityTier {
  if (explicit && explicit !== "auto" && isQualityTier(explicit)) return explicit;
  return readStoredTier() ?? detectQualityTier();
}

export function budgetsFor(tier: QualityTier): QualityBudgets {
  return budgets[tier] ?? budgets.medium;
}
